import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { useUser } from '../auth'
import { useAppContext } from '../context/AppContext'
import { trackAnalyticsEvent } from '../server/server'
import { AUTHENTICATED_ROUTE_DEFINITIONS } from './routeDefinitions'
import { advanceAnalyticsHistory } from './history'
import { buildAnalyticsPayload } from './payload'
import { trackedSurfaceIdentity } from './contentIdentity'

const AnalyticsContext = createContext()

export function AnalyticsProvider({ children, enabled = true }) {
    const { user, isSignedIn } = useUser()
    const { appState } = useAppContext()
    const location = useLocation()
    const historyRef = useRef([])
    const routeIdentityRef = useRef(null)

    useLayoutEffect(() => {
        historyRef.current = []
        routeIdentityRef.current = null
    }, [user?.id])

    const trackEvent = useCallback(
        (event, metadata = {}) => {
            if (!enabled || !isSignedIn || !user?.id || !event?.name) return

            const { snapshot, nextHistory } = advanceAnalyticsHistory(historyRef.current, event.name)
            historyRef.current = nextHistory
            const payload = buildAnalyticsPayload(event, metadata)
            trackAnalyticsEvent({ name: event.name, history: snapshot, payload }).catch(() => {})
        },
        [enabled, isSignedIn, user?.id]
    )

    useLayoutEffect(() => {
        if (!enabled || !isSignedIn) return
        const route = AUTHENTICATED_ROUTE_DEFINITIONS.find((candidate) => matchPath(location.pathname, { path: candidate.path, exact: candidate.exact }))
        if (!route) return
        const match = matchPath(location.pathname, { path: route.path, exact: route.exact })
        const context = { location, match, primaryUI: !!appState?.primary }
        const event = route.resolveEvent?.(context) || route.analytics
        if (!event) return
        const metadata = route.resolveAnalytics?.(context) || {}
        const identity = trackedSurfaceIdentity(event.name, metadata.contentId, location.pathname)
        if (routeIdentityRef.current === identity) return
        routeIdentityRef.current = identity
        trackEvent(event, metadata)
    }, [appState?.primary, enabled, isSignedIn, location, trackEvent])

    const value = useMemo(() => ({ trackEvent }), [trackEvent])
    return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>
}

export function useAnalytics() {
    const context = useContext(AnalyticsContext)
    if (!context) throw new Error('useAnalytics must be used within AnalyticsProvider')
    return context
}
